import { useEffect, useMemo, useState } from "react";
import { AvailableAgent } from "types";
import { X } from "lucide-react";
import PureDropDown, { DropdownOption } from "components/pure-dropdown";
import { useTranslation } from "react-i18next";

interface AssignSpecialistsFormProps {
  allSpecialists: AvailableAgent[];
  assignedAgents: AvailableAgent[];
  onAssign: (specialistIds: string[]) => Promise<void>;
  isSaving?: boolean;
}

const AssignSpecialistsForm = ({
  allSpecialists,
  assignedAgents,
  onAssign,
  isSaving = false,
}: AssignSpecialistsFormProps) => {
  const { t } = useTranslation();
  const [selectedSpecialistIds, setSelectedSpecialistIds] = useState<string[]>(
    []
  );

  // Get available specialists (not already assigned)
  const availableSpecialists = useMemo(() => {
    const assignedIds = new Set(assignedAgents.map((a) => a.id));
    return allSpecialists.filter((s) => !assignedIds.has(s.id));
  }, [allSpecialists, assignedAgents]);

  // Get selected specialist objects (from allSpecialists, not just available ones)
  // This ensures selected specialists are still shown even if they're being assigned
  const selectedSpecialists = useMemo(() => {
    return allSpecialists.filter((s) => selectedSpecialistIds.includes(s.id));
  }, [allSpecialists, selectedSpecialistIds]);

  // Convert available specialists to dropdown options
  const dropdownOptions: DropdownOption[] = useMemo(() => {
    return availableSpecialists.map((specialist) => ({
      value: specialist.id,
      label: specialist.name,
    }));
  }, [availableSpecialists]);

  const handleDropdownChange = (value: string | string[]) => {
    setSelectedSpecialistIds(Array.isArray(value) ? value : [value]);
  };

  const removeSelectedSpecialist = (specialistId: string) => {
    setSelectedSpecialistIds((prev) =>
      prev.filter((id) => id !== specialistId)
    );
  };

  const handleAssign = async () => {
    if (selectedSpecialistIds.length === 0) return;

    try {
      await onAssign(selectedSpecialistIds);
      setSelectedSpecialistIds([]);
    } catch (err) {
      // Error handling is done in parent component
      console.error("Error assigning specialists:", err);
    }
  };

  // Reset selection when component unmounts or when assignedAgents changes (after successful assign)
  useEffect(() => {
    return () => {
      setSelectedSpecialistIds([]);
    };
  }, [assignedAgents]);

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(50vh - 140px)", maxHeight: "calc(100vh - 280px)" }}
    >
      {/* Dropdown Field - Fixed at top */}
      <div className="mb-4 flex-shrink-0">
        <PureDropDown
          options={dropdownOptions}
          value={selectedSpecialistIds}
          onChange={handleDropdownChange}
          placeholder={t("agents.assignedSpecialists.selectSpecialists")}
          multiple={true}
          showClearButton={false}
          className="w-full"
          buttonClassName="rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        />
      </div>

      {/* Selected Specialists Chips - Scrollable */}
      <div className="-mr-2 min-h-0 flex-1 space-y-2 overflow-y-auto pr-2">
        {selectedSpecialists.length > 0 ? (
          selectedSpecialists.map((specialist) => (
            <div
              key={specialist.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-gray-600 dark:bg-gray-800"
            >
              <span className="text-sm text-gray-700 dark:text-gray-200">
                {specialist.name}
              </span>
              <button
                type="button"
                onClick={() => removeSelectedSpecialist(specialist.id)}
                className="ml-2 rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={`Remove ${specialist.name}`}
              >
                <X size={16} />
              </button>
            </div>
          ))
        ) : (
          <div className="flex justify-center py-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t("agents.assignedSpecialists.noSpecialistsSelected")}
            </span>
          </div>
        )}
      </div>

      {/* Dialog Actions - Fixed at bottom */}
      <div className="mt-4 flex flex-shrink-0 justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        <button
          onClick={handleAssign}
          disabled={selectedSpecialistIds.length === 0 || isSaving}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
        >
          {isSaving
            ? t("agents.assignedSpecialists.assigning")
            : t("agents.assignedSpecialists.assign")}
        </button>
      </div>
    </div>
  );
};

export default AssignSpecialistsForm;
